import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { Loader2, Camera, ExternalLink, Save, ShieldCheck, Star, Eye, AlertTriangle, Trash2, Copy } from "lucide-react";
import { Link } from "react-router-dom";

const CONSULT_OPTIONS = [
    { id: "phone", label: "Phone" },
    { id: "video", label: "Video" },
    { id: "in-person", label: "In-person" },
];

const LANG_OPTIONS = ["English", "Spanish", "French", "Mandarin", "Cantonese", "Vietnamese", "Korean", "Arabic", "Portuguese", "Russian", "Tagalog", "Haitian Creole"];

// Tier badge — explicitly NEUTRAL, never stars, so attorneys understand the
// distinction from client ratings.
const TIER_LABEL = { basic: "Basic", featured: "Pro", top_spot: "Elite" };
const TIER_BADGE_STYLE = {
    basic: "bg-muted text-foreground/80",
    featured: "bg-primary/10 text-primary border border-primary/30",
    top_spot: "bg-amber-200/40 text-amber-900 border border-amber-500/40 dark:bg-amber-500/20 dark:text-amber-200",
};

/**
 * AttorneyProfileForm — the "Profile" tab in the attorney dashboard.
 * Pre-fills with the attorney's saved profile, posts back via PUT /attorney/profile.
 * Photo upload is a separate multipart call to POST /attorney/profile/photo.
 */
export default function AttorneyProfileForm({ userId, userName }) {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [photoBusy, setPhotoBusy] = useState(false);
    const [visibility, setVisibility] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteForm, setShowDeleteForm] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState("");
    const [categories, setCategories] = useState([]);
    const [tier, setTier] = useState("");
    const [form, setForm] = useState({
        firm_name: "", phone: "", email_public: "", website: "", bio: "",
        years_experience: "", service_area_city: "", service_area_state: "",
        jurisdictions: "", bar_number: "", notable_results: "",
        practice_area_ids: [], languages: [], consultation_options: [],
        photo_url: "",
    });

    const fileInputRef = useRef(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [profileRes, catsRes, publicRes, visRes] = await Promise.all([
                    api.get("/attorney/profile"),
                    api.get("/categories"),
                    userId ? api.get(`/attorneys/${userId}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
                    api.get("/attorney/visibility").catch(() => ({ data: null })),
                ]);
                if (!alive) return;
                setCategories(catsRes.data || []);
                setVisibility(visRes.data);
                const p = profileRes.data?.profile || {};
                setForm({
                    firm_name: p.firm_name || "",
                    phone: p.phone || "",
                    email_public: p.email_public || "",
                    website: p.website || "",
                    bio: p.bio || "",
                    years_experience: p.years_experience ?? "",
                    service_area_city: p.service_area_city || "",
                    service_area_state: p.service_area_state || "",
                    jurisdictions: (p.jurisdictions || []).join(", "),
                    bar_number: p.bar_number || "",
                    notable_results: p.notable_results || "",
                    practice_area_ids: p.practice_area_ids || [],
                    languages: p.languages || [],
                    consultation_options: p.consultation_options || [],
                    photo_url: p.photo_url || "",
                });
                setTier(publicRes.data?.profile_tier || "");
            } catch (e) {
                toast.error("Couldn't load your profile.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [userId]);

    const toggleArr = (key, value) => {
        setForm((f) => {
            const cur = new Set(f[key] || []);
            if (cur.has(value)) cur.delete(value); else cur.add(value);
            return { ...f, [key]: Array.from(cur) };
        });
    };

    const onUploadPhoto = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("Image too large — 10 MB max."); return; }
        if (!file.type.startsWith("image/")) { toast.error("Please pick an image file."); return; }
        setPhotoBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/attorney/profile/photo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, photo_url: data.photo_url }));
            toast.success("Photo updated.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Photo upload failed.");
        } finally { setPhotoBusy(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                firm_name: form.firm_name,
                phone: form.phone,
                email_public: form.email_public,
                website: form.website,
                bio: form.bio,
                years_experience: form.years_experience === "" ? null : Number(form.years_experience),
                service_area_city: form.service_area_city,
                service_area_state: form.service_area_state,
                jurisdictions: form.jurisdictions.split(",").map((s) => s.trim()).filter(Boolean),
                bar_number: form.bar_number,
                notable_results: form.notable_results,
                practice_area_ids: form.practice_area_ids,
                languages: form.languages,
                consultation_options: form.consultation_options,
            };
            await api.put("/attorney/profile", payload);
            toast.success("Profile saved.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed.");
        } finally { setSaving(false); }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12" data-testid="profile-loading">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="attorney-profile-form">
            {/* Top strip — tier badge + clear separation from client ratings */}
            <div className="ll-card flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    {tier ? (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${TIER_BADGE_STYLE[tier]}`} data-testid="dashboard-tier-badge">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            {TIER_LABEL[tier]} profile tier
                        </span>
                    ) : (
                        <span className="text-xs text-muted-foreground">No active placement yet — your public profile will show no tier badge until you buy a placement.</span>
                    )}
                    <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                        Client star ratings are separate and submitted by clients.
                    </span>
                </div>
                {userId && (
                    <Link
                        to={`/attorney/${userId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-3 py-1.5 hover:bg-accent"
                        data-testid="view-public-profile-link">
                        View public profile <ExternalLink className="h-3 w-3" />
                    </Link>
                )}
            </div>

            {/* Photo */}
            <section className="ll-card" data-testid="profile-photo-section">
                <h3 className="font-serif text-lg font-semibold mb-4">Professional headshot</h3>
                <div className="flex items-center gap-5 flex-wrap">
                    {form.photo_url ? (
                        <img src={form.photo_url} alt="Profile" className="w-28 h-28 rounded-2xl object-cover border-2 border-border" data-testid="profile-photo-preview" />
                    ) : (
                        <div className="w-28 h-28 rounded-2xl bg-muted flex items-center justify-center border-2 border-border text-3xl font-serif text-muted-foreground" data-testid="profile-photo-placeholder">
                            {userName?.[0] || "?"}
                        </div>
                    )}
                    <div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={onUploadPhoto}
                            data-testid="profile-photo-input"
                        />
                        <button
                            type="button"
                            disabled={photoBusy}
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-60"
                            data-testid="profile-photo-upload-btn">
                            {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                            {form.photo_url ? "Replace photo" : "Upload photo"}
                        </button>
                        <p className="text-xs text-muted-foreground mt-2">JPG, PNG, WEBP, or HEIC — up to 10 MB. Use a clear professional headshot.</p>
                    </div>
                </div>
            </section>

            {/* Identity */}
            <section className="ll-card" data-testid="profile-identity-section">
                <h3 className="font-serif text-lg font-semibold mb-4">Identity</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="ll-label block mb-1.5">Full name</label>
                        <input className="ll-input bg-muted/40" value={userName || ""} disabled data-testid="profile-name-readonly" />
                        <p className="text-[11px] text-muted-foreground mt-1">Edit your display name from the account menu.</p>
                    </div>
                    <div>
                        <label className="ll-label block mb-1.5">Firm / practice name</label>
                        <input className="ll-input" value={form.firm_name} onChange={(e) => setForm((f) => ({ ...f, firm_name: e.target.value }))} data-testid="profile-firm-input" />
                    </div>
                    <div>
                        <label className="ll-label block mb-1.5">Phone</label>
                        <input className="ll-input" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="(404) 555-0102" data-testid="profile-phone-input" />
                    </div>
                    <div>
                        <label className="ll-label block mb-1.5">Public email</label>
                        <input className="ll-input" type="email" value={form.email_public} onChange={(e) => setForm((f) => ({ ...f, email_public: e.target.value }))} placeholder="hello@yourfirm.com" data-testid="profile-email-input" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="ll-label block mb-1.5">Website</label>
                        <input className="ll-input" type="url" value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} placeholder="https://yourfirm.com" data-testid="profile-website-input" />
                    </div>
                </div>
            </section>

            {/* About */}
            <section className="ll-card" data-testid="profile-about-section">
                <h3 className="font-serif text-lg font-semibold mb-4">About</h3>
                <label className="ll-label block mb-1.5">Bio</label>
                <textarea
                    rows={5}
                    className="ll-input resize-none mb-4"
                    placeholder="Tell prospective clients about your practice — who you help, your approach, what makes your firm a good fit."
                    value={form.bio}
                    onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                    data-testid="profile-bio-input"
                />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                        <label className="ll-label block mb-1.5">Years of experience</label>
                        <input className="ll-input" type="number" min="0" max="80" value={form.years_experience} onChange={(e) => setForm((f) => ({ ...f, years_experience: e.target.value }))} data-testid="profile-years-input" />
                    </div>
                    <div>
                        <label className="ll-label block mb-1.5">Service area — city</label>
                        <input className="ll-input" value={form.service_area_city} onChange={(e) => setForm((f) => ({ ...f, service_area_city: e.target.value }))} placeholder="Atlanta" data-testid="profile-city-input" />
                    </div>
                    <div>
                        <label className="ll-label block mb-1.5">Service area — state</label>
                        <input className="ll-input uppercase tracking-wider" maxLength={2} value={form.service_area_state} onChange={(e) => setForm((f) => ({ ...f, service_area_state: e.target.value.toUpperCase() }))} placeholder="GA" data-testid="profile-state-input" />
                    </div>
                </div>
            </section>

            {/* Practice areas */}
            <section className="ll-card" data-testid="profile-practice-section">
                <h3 className="font-serif text-lg font-semibold mb-4">Practice areas</h3>
                <div className="flex flex-wrap gap-2" data-testid="practice-area-chips">
                    {categories.map((c) => {
                        const on = form.practice_area_ids.includes(c.id);
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => toggleArr("practice_area_ids", c.id)}
                                data-testid={`practice-${c.slug}`}
                                data-on={on ? "true" : "false"}
                                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${on
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background text-foreground border-border hover:bg-accent/60"}`}>
                                {c.name}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Licensing */}
            <section className="ll-card" data-testid="profile-licensing-section">
                <h3 className="font-serif text-lg font-semibold mb-4">Licensing & jurisdictions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="ll-label block mb-1.5">Jurisdictions <span className="text-muted-foreground/70 font-normal">(comma-separated)</span></label>
                        <input className="ll-input" value={form.jurisdictions} onChange={(e) => setForm((f) => ({ ...f, jurisdictions: e.target.value }))} placeholder="GA Bar, 11th Circuit" data-testid="profile-jurisdictions-input" />
                    </div>
                    <div>
                        <label className="ll-label block mb-1.5">Bar number</label>
                        <input className="ll-input" value={form.bar_number} onChange={(e) => setForm((f) => ({ ...f, bar_number: e.target.value }))} data-testid="profile-bar-input" />
                    </div>
                </div>
            </section>

            {/* Languages */}
            <section className="ll-card" data-testid="profile-languages-section">
                <h3 className="font-serif text-lg font-semibold mb-4">Languages spoken</h3>
                <div className="flex flex-wrap gap-2">
                    {LANG_OPTIONS.map((lang) => {
                        const on = form.languages.includes(lang);
                        return (
                            <button
                                key={lang}
                                type="button"
                                onClick={() => toggleArr("languages", lang)}
                                data-testid={`lang-${lang.toLowerCase()}`}
                                data-on={on ? "true" : "false"}
                                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${on
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background text-foreground border-border hover:bg-accent/60"}`}>
                                {lang}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* Notable results */}
            <section className="ll-card" data-testid="profile-results-section">
                <h3 className="font-serif text-lg font-semibold mb-2">Notable results / representative cases</h3>
                <p className="text-xs text-muted-foreground mb-3">List a handful of representative outcomes. Be careful not to disclose confidential client information. Past results don't guarantee future outcomes.</p>
                <textarea
                    rows={4}
                    className="ll-input resize-none"
                    placeholder="• Defended a small-business owner against breach-of-contract suit — case dismissed&#10;• Negotiated $250,000 settlement in workplace discrimination matter&#10;…"
                    value={form.notable_results}
                    onChange={(e) => setForm((f) => ({ ...f, notable_results: e.target.value }))}
                    data-testid="profile-results-input"
                />
            </section>

            {/* Consultation options */}
            <section className="ll-card" data-testid="profile-consult-section">
                <h3 className="font-serif text-lg font-semibold mb-4">Consultation options</h3>
                <div className="flex flex-wrap gap-2">
                    {CONSULT_OPTIONS.map((o) => {
                        const on = form.consultation_options.includes(o.id);
                        return (
                            <button
                                key={o.id}
                                type="button"
                                onClick={() => toggleArr("consultation_options", o.id)}
                                data-testid={`consult-${o.id}`}
                                data-on={on ? "true" : "false"}
                                className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${on
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background text-foreground border-border hover:bg-accent/60"}`}>
                                {o.label}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* WHERE I'M SHOWING UP */}
            {visibility && (
                <section className="ll-card" data-testid="where-im-showing-up">
                    <header className="flex items-center gap-2 mb-1">
                        <Eye className="h-5 w-5 text-primary" />
                        <h3 className="font-serif text-lg font-semibold">Where I'm showing up</h3>
                    </header>
                    <p className="text-xs text-muted-foreground mb-4">A live, transparent view of every directory page your profile currently appears on.</p>

                    {visibility.downgrade_notice && (
                        <div className="flex items-start gap-2 mb-4 rounded-lg border border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 px-3 py-2.5" data-testid="visibility-downgrade-notice">
                            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">{visibility.downgrade_notice}</div>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="text-xs text-muted-foreground">Plan:</span>
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${TIER_BADGE_STYLE[visibility.plan_tier] || TIER_BADGE_STYLE.basic}`} data-testid="visibility-plan-tier">
                            <ShieldCheck className="h-3.5 w-3.5" />
                            {TIER_LABEL[visibility.plan_tier] || "Basic"} profile tier
                        </span>
                        <span className="text-xs text-muted-foreground">Status:</span>
                        <span
                            className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${visibility.live_status === "live"
                                ? "bg-green-500/10 text-green-700 border border-green-500/30"
                                : visibility.live_status === "basic_listing"
                                    ? "bg-amber-500/10 text-amber-700 border border-amber-500/30"
                                    : "bg-muted text-muted-foreground border border-border"}`}
                            data-testid="visibility-live-status">
                            {visibility.live_status === "live" ? "Live — Featured" :
                             visibility.live_status === "basic_listing" ? "Live — Basic" : "No placements"}
                        </span>
                    </div>

                    {visibility.placements.length === 0 && visibility.basic_fallback_urls?.length === 0 ? (
                        <div className="text-xs text-muted-foreground">You have no placements yet. Visit the Placements tab to choose a county + practice area combination to appear on.</div>
                    ) : (
                        <ul className="space-y-2" data-testid="visibility-placements">
                            {visibility.placements.map((p) => (
                                <li key={p.id} className="rounded-lg border border-border bg-background/60 px-3 py-2.5">
                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                        <div>
                                            <div className="text-sm font-medium">
                                                {p.county_name}{p.state_code && `, ${p.state_code}`} · {p.practice_area_name}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                                Tier: <span className="font-medium">{TIER_LABEL[p.tier] || "Basic"}</span>
                                                <span className="mx-1.5">·</span>
                                                Status: <span className="font-medium">{p.status}</span>
                                                {p.expires_at && (<><span className="mx-1.5">·</span>Expires {new Date(p.expires_at).toLocaleDateString()}</>)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`placement-urls-${p.id}`}>
                                        {p.urls.map((u) => (
                                            <button
                                                key={u}
                                                type="button"
                                                onClick={() => { navigator.clipboard.writeText(window.location.origin + u); toast.success("Link copied"); }}
                                                className="inline-flex items-center gap-1 text-[11px] rounded-full bg-accent/60 border border-border px-2 py-0.5 hover:bg-accent font-mono"
                                                data-testid={`placement-url-${u.replace(/\//g, '-')}`}
                                                title={`${window.location.origin}${u} — click to copy`}>
                                                <Copy className="h-2.5 w-2.5" /> {u}
                                            </button>
                                        ))}
                                    </div>
                                </li>
                            ))}
                            {visibility.basic_fallback_urls?.map((u) => (
                                <li key={u} className="rounded-lg border border-border bg-background/60 px-3 py-2.5">
                                    <div className="text-sm font-medium">Basic listing (no paid placement)</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">You appear via your declared service area only.</div>
                                    <button
                                        type="button"
                                        onClick={() => { navigator.clipboard.writeText(window.location.origin + u); toast.success("Link copied"); }}
                                        className="mt-2 inline-flex items-center gap-1 text-[11px] rounded-full bg-accent/60 border border-border px-2 py-0.5 hover:bg-accent font-mono">
                                        <Copy className="h-2.5 w-2.5" /> {u}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {/* DELETE ACCOUNT */}
            <section className="ll-card border-destructive/30" data-testid="delete-account-section">
                <header className="flex items-center gap-2 mb-1">
                    <Trash2 className="h-5 w-5 text-destructive" />
                    <h3 className="font-serif text-lg font-semibold text-destructive">Delete account</h3>
                </header>
                <p className="text-xs text-muted-foreground mb-4">
                    Your public profile and client reviews will be hidden immediately. Your account record is retained for 30 days
                    so you can email us to reactivate. After that window, all data is permanently removed.
                </p>
                {!showDeleteForm ? (
                    <button
                        type="button"
                        onClick={() => setShowDeleteForm(true)}
                        className="inline-flex items-center gap-2 rounded-full border border-destructive text-destructive px-4 py-2 text-sm font-semibold hover:bg-destructive/10"
                        data-testid="open-delete-account-btn">
                        <Trash2 className="h-4 w-4" /> Delete my account…
                    </button>
                ) : (
                    <div className="space-y-3">
                        <label className="ll-label block">Type your account email to confirm</label>
                        <input
                            className="ll-input"
                            type="email"
                            placeholder="your@email.com"
                            value={confirmEmail}
                            onChange={(e) => setConfirmEmail(e.target.value)}
                            data-testid="delete-confirm-email"
                        />
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                disabled={deleting || !confirmEmail.trim()}
                                onClick={async () => {
                                    setDeleting(true);
                                    try {
                                        await api.post("/account/delete", { confirm_email: confirmEmail.trim() });
                                        toast.success("Account deactivated. You will be logged out.");
                                        await new Promise((r) => setTimeout(r, 800));
                                        logout();
                                        navigate("/");
                                    } catch (e) {
                                        toast.error(e?.response?.data?.detail || "Couldn't deactivate. Please try again.");
                                    } finally { setDeleting(false); }
                                }}
                                className="inline-flex items-center gap-2 rounded-full bg-destructive text-destructive-foreground px-4 py-2 text-sm font-semibold hover:bg-destructive/90 disabled:opacity-60"
                                data-testid="confirm-delete-account-btn">
                                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                Permanently deactivate
                            </button>
                            <button
                                type="button"
                                onClick={() => { setShowDeleteForm(false); setConfirmEmail(""); }}
                                className="text-xs text-muted-foreground hover:text-foreground"
                                data-testid="cancel-delete-account-btn">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {/* Save bar */}
            <div className="sticky bottom-2 z-10 flex justify-end" data-testid="profile-save-bar">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold shadow-lg hover:bg-primary/90 disabled:opacity-60"
                    data-testid="profile-save-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save profile
                </button>
            </div>
        </div>
    );
}
